<template>
    <div class="panel panel-default" data-testid="order-detail-timeline">
        <div class="panel-heading od-timeline__heading">
            <h4>{{ order.ui.labels.timeline }}</h4>
            <div class="od-timeline__legend" data-testid="order-detail-timeline-legend">
                <span class="od-timeline__legend-item"><span class="od-timeline__legend-dot is-saga" />Saga</span>
                <span class="od-timeline__legend-item"><span class="od-timeline__legend-dot is-lifecycle" />Lifecycle</span>
            </div>
        </div>
        <div class="panel-body od-timeline__body">
            <p v-if="!events.length" class="text-muted od-empty-inline">{{ emptyLabel }}</p>
            <ol v-else class="od-timeline">
                <li
                    v-for="(row, index) in rows"
                    :key="eventKey(row.event, index)"
                    class="od-timeline__item"
                    :data-testid="'order-detail-timeline-event-' + timelineEventId(row.event)"
                >
                    <p v-if="row.showDay" class="od-timeline__day">{{ row.day }}</p>
                    <div class="od-timeline__entry">
                        <span class="od-timeline__dot" :class="dotClass(row.event)" />
                        <div class="od-timeline__grid">
                            <div class="od-timeline__content">
                                <h3
                                    class="od-timeline__title"
                                    :class="{ 'is-gap': row.event.gap }"
                                    :title="rawTitle(row.event)"
                                >
                                    {{ eventTitle(row.event) }}
                                    <a
                                        v-if="eventActionHref(row.event)"
                                        :href="eventActionHref(row.event)"
                                        class="btn btn-xs btn-default od-timeline__chip"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >{{ eventActionLabel(row.event) }}</a>
                                </h3>
                                <p v-if="eventBody(row.event)" class="od-timeline__text">{{ eventBody(row.event) }}</p>
                            </div>
                            <od-status-badge :status="row.event.status || ''" />
                            <time class="od-timeline__time">{{ row.time }}</time>
                        </div>
                    </div>
                </li>
            </ol>
        </div>
    </div>
</template>

<script>
import OdStatusBadge from './OdStatusBadge.vue';
import { eventLabel } from './orderDetailHelpers';

const LIFECYCLE_SOURCE = 'order_lifecycle_events';

export default {
    name: 'OrderTimeline',
    components: { OdStatusBadge },
    props: {
        order: {
            type: Object,
            required: true,
        },
    },
    computed: {
        events() {
            const activities = this.order.activities;
            if (Array.isArray(activities)) {
                return activities;
            }

            const timeline = this.order.timeline;
            if (!Array.isArray(timeline)) {
                return [];
            }

            return timeline.filter((event) => !event.gap);
        },
        rows() {
            let previousDay = null;

            return this.events.map((event) => {
                const parsed = this.parseDate(event.occurredAt || event.createdAt);
                const day = parsed
                    ? new Intl.DateTimeFormat('it-IT', { dateStyle: 'full' }).format(parsed)
                    : 'Data non disponibile';
                const time = parsed
                    ? new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(parsed)
                    : '—';
                const showDay = day !== previousDay;
                previousDay = day;

                return { event, day, time, showDay };
            });
        },
        emptyLabel() {
            return this.order.ui?.labels?.activitiesEmpty || 'Nessuna attività registrata';
        },
    },
    methods: {
        parseDate(raw) {
            if (!raw) {
                return null;
            }
            const d = new Date(raw);

            return Number.isNaN(d.getTime()) ? null : d;
        },
        timelineEventId(event) {
            if (event.id != null) {
                return String(event.id).replace(/[^a-zA-Z0-9_-]/g, '-');
            }
            return 'unknown';
        },
        eventKey(event, index) {
            return event.id != null ? String(event.id) : `e${index}`;
        },
        rawTitle(event) {
            return event.description || event.title || '';
        },
        eventTitle(event) {
            return eventLabel(this.rawTitle(event) || 'Attività');
        },
        isLifecycle(event) {
            return event.source === LIFECYCLE_SOURCE;
        },
        eventBody(event) {
            if (event.reason) {
                return event.reason;
            }
            if (this.isLifecycle(event) && event.fromValue && event.toValue) {
                return `${event.fromValue} → ${event.toValue}`;
            }

            return event.body || event.actorLabel || '';
        },
        eventActionHref(event) {
            return event.actionUrl || event.linkUrl || null;
        },
        eventActionLabel(event) {
            return event.actionLabel || 'Apri';
        },
        dotClass(event) {
            if (event.gap) {
                return 'is-gap';
            }

            return this.isLifecycle(event) ? 'is-lifecycle' : 'is-saga';
        },
    },
};
</script>
